import { defineStore } from 'pinia';
import axios from 'axios';
import { useNotificationStore } from '@/store/global/notification';
import { useUsersStore } from '@/store/global/users';
import { useAiBrandMonitorStore } from '@/store/visibility/aiBrandMonitor';
import { resolveCreationDate } from '@/utils/geo/dateRanges';

export const useAverageKeywordStore = defineStore('averageKeyword', {
	state: () => ({
		keywords: [],
		core_updates: [],
		ai_score_evolution: [],
		custom_markers: []
	}),
	getters: {
		clientPosition(state) {
			return state.keywords.map(item => item.avg_client_position);
		},
		// competitorSeries(state) {
		// 	const competitorSeries = [];
		//
		// 	for (let i = 1; i <= 5; i++) {
		// 		const competitorName = state.keywords[0][`competitor${i}`].name;
		// 		const competitorPositions = state.keywords.map(item => item[`competitor${i}`].avg_position);
		// 		competitorSeries.push({
		// 			name: competitorName,
		// 			data: competitorPositions,
		// 		});
		// 	}
		//
		// 	return competitorSeries;
		// },
		// creationDates(state) {
		// 	return state.keywords.map(item => {
		// 		const date = new Date(item.creation_date * 1000);
		// 		return date.toLocaleDateString('en-GB', {
		// 			day: '2-digit',
		// 			month: '2-digit',
		// 			year: 'numeric',
		// 		});
		// 	});
		// },
		competitorSeries(state) {
			const competitorSeries = [];
			if (state.keywords[0] != undefined && state.keywords[0].competitors) {
				// Sort competitors by order to maintain consistent display
				const sortedCompetitors = state.keywords[0].competitors.sort((a, b) => a.order - b.order);

				sortedCompetitors.forEach(competitor => {
					const competitorPositions = state.keywords.map(item => {
						const competitorData = item.competitors.find(c => c.name === competitor.name);
						return competitorData ? competitorData.avg_position : null;
					});

					competitorSeries.push({
						name: competitor.name,
						data: competitorPositions,
						color: competitor.color
					});
				});
			}

			return competitorSeries;
		},
		// creationDates(state) {
		//     return state.keywords.map(item => {
		//         const date = new Date(item.creation_date * 1000);
		//         return date.toLocaleDateString('en-GB', {
		//             day: '2-digit',
		//             month: '2-digit',
		//             year: 'numeric'
		//         });
		//     });
		// },
		creationDates(state) {
			return state.keywords.map(item => {
				return new Date(item.creation_date * 1000).getTime();
			});
		},
		coreUpdates(state) {
			return state.core_updates;
		},
		aiScoreEvolution(state) {
			return state.ai_score_evolution;
		},
	customMarkers(state) {
		const googleMarkers = (state.core_updates || []).map((u, idx) => ({
			id: `core-${idx}-${u.date}`,
			marker_date: u.date, // expected YYYY-MM-DD
			title: '',
			description: u.text,
			type: 'google',
			readonly: true
		}))
		return [...(state.custom_markers || []), ...googleMarkers];
	}
	},
	actions: {
		async fetchData({ market_id, project_id, branding = true, filter = null, from = null, to = null, competitor_label_id = null }) {
			this.keywords = [];
			let date_from = '';
			let date_to = '';

			if (!from || !to) {
				const selected_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));

				date_from = selected_dates.from;
				date_to = selected_dates.to;
			} else {
				date_from = from;
				date_to = to;
			}

			// Parse DD-MM-YYYY format to a valid date
			const parseDateString = (dateStr) => {
				const [day, month, year] = dateStr.split('-');
				return `${year}-${month}-${day}`; // Convert to YYYY-MM-DD for proper date parsing
			};

			const options = {
				market_id: market_id,
				project_id: project_id,
				branding: branding,
				date_range_start: parseDateString(date_from),
				date_range_end: parseDateString(date_to),
				competitor_label_id: competitor_label_id,
			};

			if (filter) {
				options.filter = JSON.stringify(filter);
			}

			const response = await axios.post('vuejs/get_average_keywords', options, { withCredentials: true });
			this.keywords = response.data.data.keywords;
			return response.data.data.keywords;
		},
		async fetchCoreUpdates(marketId) {
			const response = await axios.get('vuejs/grab_core_updates', {
				params: { market_id: marketId },
				withCredentials: true
			});
			this.core_updates = response.data.data;
		},
		async fetchAiScoreEvolution(marketId, date_from, date_to, competitor_label_id = null, label_ids = null) {
			const aiBrandMonitorStore = useAiBrandMonitorStore();
			const creationDateFrom = resolveCreationDate(date_from, aiBrandMonitorStore.dates);
			const creationDateTo = resolveCreationDate(date_to, aiBrandMonitorStore.dates);

			const response = await axios.post('vuejs/geo_scores_actor_evolution', {
				market_id: marketId,
				date_range_start: creationDateFrom ?? date_from,
				date_range_end: creationDateTo ?? date_to,
				ai_name: 'chatgpt',
				competitor_label_id: competitor_label_id,
				label_ids: label_ids,
			},
			{ withCredentials: true });
			this.ai_score_evolution = response.data.data;
		},
		clearChartData() {
			const hadData = this.keywords.length > 0 || this.custom_markers.length > 0 || this.core_updates.length > 0;
			this.keywords = [];
			this.custom_markers = [];
			this.core_updates = [];
		},
		async addCustomMarker(marketId, markerData) {
			const notification = useNotificationStore();

			try {
				const payload = {
					market_id: marketId,
					marker_date: markerData.marker_date || markerData.markerDate,
					comment: markerData.comment || markerData.description || markerData.title || ''
				}
				const response = await axios.post('vuejs/create_custom_marker', payload, { withCredentials: true });

				if (response.data.success === 'success') {
					notification.open('success', 'Custom marker created successfully');
					return {
						success: true,
						data: response.data.data
					};
				} else {
					notification.open('error', response.data.message || 'Failed to create custom marker');
					return {
						success: false,
						message: response.data.message
					};
				}
			} catch (error) {
				const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
				notification.open('error', `Error: ${errorMessage}`);
				console.error('Failed to create custom marker:', error);
				return {
					success: false,
					message: errorMessage
				};
			}
		},
		async fetchCustomMarkers(marketId) {
			try {
				const response = await axios.get('vuejs/get_custom_markers', {
					params: { market_id: marketId },
					withCredentials: true
				});
				this.custom_markers = response.data.data || [];
			} catch (error) {
				console.error(t('modal_custom_marker.failed_to_fetch_markers'), error);
				this.custom_markers = [];
			}
		},
		async updateCustomMarker(markerId, markerData) {
			try {
				const response = await axios.put('vuejs/update_custom_marker', {
					marker_id: markerId,
					marker_date: markerData.marker_date || markerData.markerDate,
					comment: markerData.comment || markerData.description || markerData.title || ''
				}, { withCredentials: true });
				return response.data;
			} catch (error) {
				console.error(t('modal_custom_marker.failed_to_update_marker'), error);
				return false;
			}
		},
		async deleteCustomMarker(markerId) {
			try {
				const response = await axios.delete('vuejs/delete_custom_marker', {
					data: { marker_id: markerId },
					withCredentials: true
				});
				return response.data;
			} catch (error) {
				console.error(t('modal_custom_marker.failed_to_delete_marker'), error);
				return false;
			}
		},
		async getUserData(userId) {
			try {
				const usersStore = useUsersStore();
				const user = await usersStore.getUserById(userId);

				if (user) {
					return {
						success: true,
						data: {
							full_name: user.full_name,
							image: user.avatar
						}
					};
				}
				return {
					success: false,
					message: 'User not found'
				};
			} catch (error) {
				console.error(t('modal_custom_marker.failed_to_fetch_user_data'), error);
				return {
					success: false,
					message: error.message || 'Failed to fetch user data'
				};
			}
		}
	},
	persist: false,
});
